// src/pages/Contact.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Contact = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [currentTime, setCurrentTime] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    status: 'pending',
    type: 'general'
  });

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'contacts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const contactData = [];
      snapshot.forEach((doc) => {
        contactData.push({ id: doc.id, ...doc.data() });
      });
      setContacts(contactData);
      setLoading(false);
    });

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
    };
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      status: 'pending',
      type: 'general'
    });
    setEditingContact(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      subject: contact.subject || '',
      message: contact.message || '',
      status: contact.status || 'pending',
      type: contact.type || 'general'
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const contactData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        subject: formData.subject || '',
        message: formData.message || '',
        status: formData.status,
        type: formData.type,
        updatedAt: new Date()
      };

      if (editingContact) {
        await updateDoc(doc(db, 'contacts', editingContact.id), contactData);
        alert('Contact updated successfully!');
      } else {
        contactData.createdAt = new Date();
        await addDoc(collection(db, 'contacts'), contactData);
        alert('Contact added successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Error saving contact: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'contacts', id));
      alert('Contact deleted successfully!');
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Error deleting contact: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const getFilteredContacts = () => {
    if (filterType === 'all') return contacts;
    return contacts.filter(c => c.status === filterType);
  };

  const getStats = () => {
    const total = contacts.length;
    const pending = contacts.filter(c => c.status === 'pending').length;
    const resolved = contacts.filter(c => c.status === 'resolved').length;
    const inProgress = contacts.filter(c => c.status === 'in-progress').length;
    return { total, pending, resolved, inProgress };
  };

  const stats = getStats();
  const filteredContacts = getFilteredContacts();

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'Pending', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
      'in-progress': { label: 'In Progress', color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.12)' },
      'resolved': { label: 'Resolved', color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
      'spam': { label: 'Spam', color: '#f43f5e', bg: 'rgba(244, 63, 94, 0.12)' }
    };
    return statusMap[status] || statusMap['pending'];
  };

  const getTypeIcon = (type) => {
    const typeMap = {
      'general': 'fa-envelope',
      'admission': 'fa-graduation-cap',
      'complaint': 'fa-exclamation-triangle',
      'feedback': 'fa-comment',
      'support': 'fa-headset'
    };
    return typeMap[type] || 'fa-envelope';
  };

  const styles = `
    .contact-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    .filter-select {
      padding: 10px 20px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      outline: none;
      transition: all 0.2s;
    }

    .filter-select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .contacts-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
      gap: 24px;
    }

    .contact-card {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      padding: 24px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
      display: flex;
      flex-direction: column;
    }

    .contact-card:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.65);
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .contact-card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
    }

    .contact-card-header .contact-name {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.1rem;
      font-weight: 700;
      color: #0f172a;
    }

    .contact-card-header .contact-type {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 0.7rem;
      font-weight: 600;
      background: rgba(14, 165, 233, 0.12);
      color: #0ea5e9;
      border: 1px solid rgba(14, 165, 233, 0.2);
    }

    .contact-card-body {
      flex: 1;
      margin-bottom: 12px;
    }

    .contact-card-body .contact-subject {
      font-weight: 600;
      color: #0f172a;
      margin-bottom: 4px;
      font-size: 0.95rem;
    }

    .contact-card-body .contact-message {
      color: #64748b;
      font-size: 0.85rem;
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .contact-card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 12px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
      flex-wrap: wrap;
      gap: 8px;
    }

    .contact-card-footer .contact-meta {
      display: flex;
      gap: 12px;
      font-size: 0.75rem;
      color: #64748b;
    }

    .contact-card-footer .contact-meta i {
      margin-right: 4px;
    }

    .status-badge {
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      display: inline-block;
    }

    .contact-card-actions {
      display: flex;
      gap: 8px;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
    }

    .btn-row-action {
      flex: 1;
      padding: 8px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      grid-column: 1/-1;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
      color: #0f172a;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
    }

    /* Modal Styles - Positioned at Top */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 99999;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 40px 20px 80px 20px;
      overflow-y: auto;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 100%;
      max-width: 650px;
      max-height: none;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideDown 0.3s ease;
      margin-bottom: 40px;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.4rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }

    .form-group {
      margin-bottom: 4px;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .form-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 10px 24px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .contacts-grid {
        grid-template-columns: 1fr;
      }
      .form-grid {
        grid-template-columns: 1fr;
      }
      .management-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .modal-viewport-box {
        padding: 24px;
        margin: 10px;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  if (loading && contacts.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="contact-page">
        <div className="welcome-hero">
          <h1>📬 Contact Management</h1>
          <p>Manage all contact inquiries, feedback, and support requests from website visitors.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="stats-matrix" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <div className="matrix-card card-blue">
            <div>
              <div className="matrix-value">{stats.total}</div>
              <div className="matrix-label">Total Messages</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-envelope"></i></div>
          </div>
          <div className="matrix-card card-amber">
            <div>
              <div className="matrix-value">{stats.pending}</div>
              <div className="matrix-label">Pending</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-clock"></i></div>
          </div>
          <div className="matrix-card card-emerald">
            <div>
              <div className="matrix-value">{stats.resolved}</div>
              <div className="matrix-label">Resolved</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-check-circle"></i></div>
          </div>
          <div className="matrix-card card-purple">
            <div>
              <div className="matrix-value">{stats.inProgress}</div>
              <div className="matrix-label">In Progress</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-spinner"></i></div>
          </div>
        </div>

        <div className="management-toolbar">
          <div>
            <select 
              className="filter-select" 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Messages</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="spam">Spam</option>
            </select>
          </div>
          <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
            <i className="fas fa-plus-circle"></i> Add Contact
          </button>
        </div>

        <div className="contacts-grid">
          {filteredContacts.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <h3>No Messages Found</h3>
              <p>There are no contact messages matching your filter.</p>
              <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
                <i className="fas fa-plus-circle"></i> Add Contact Message
              </button>
            </div>
          ) : (
            filteredContacts.map((item) => {
              const status = getStatusBadge(item.status);
              return (
                <div className="contact-card" key={item.id}>
                  <div className="contact-card-header">
                    <div>
                      <div className="contact-name">{item.name || 'Anonymous'}</div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                        <i className="fas fa-envelope"></i> {item.email || 'No email'}
                      </div>
                    </div>
                    <span className="contact-type">
                      <i className={`fas ${getTypeIcon(item.type)}`}></i>
                      {item.type || 'general'}
                    </span>
                  </div>
                  <div className="contact-card-body">
                    <div className="contact-subject">{item.subject || 'No subject'}</div>
                    <div className="contact-message">{item.message || 'No message'}</div>
                  </div>
                  <div className="contact-card-footer">
                    <div className="contact-meta">
                      <span><i className="far fa-calendar"></i> {formatDate(item.createdAt)}</span>
                      {item.phone && <span><i className="fas fa-phone"></i> {item.phone}</span>}
                    </div>
                    <span className="status-badge" style={{ color: status.color, background: status.bg, border: `1px solid ${status.color}20` }}>
                      {status.label}
                    </span>
                  </div>
                  <div className="contact-card-actions">
                    <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(item)}>
                      <i className="fas fa-pen-to-square"></i> Edit
                    </button>
                    <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(item.id)}>
                      <i className="fas fa-trash-can"></i> Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Contact Modal - Positioned at Top */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingContact ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                  {editingContact ? 'Edit Contact Message' : 'Add New Contact Message'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email <span className="required">*</span></label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@example.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 1234567890"
                    />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="general">General</option>
                      <option value="admission">Admission</option>
                      <option value="complaint">Complaint</option>
                      <option value="feedback">Feedback</option>
                      <option value="support">Support</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Subject</label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Enter subject"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Message <span className="required">*</span></label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows="4"
                      placeholder="Enter message..."
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="spam">Spam</option>
                    </select>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingContact ? 'Update Contact' : 'Add Contact'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Contact;